import React, { useEffect, useRef, useState } from 'react';
import { applyViewerMode, invokeModeAction } from './utils/viewerHelpers';
import { fetchIfcSelection, isIfcFile } from './utils/ifcSelection';
import { readViewerColors, observeTheme } from './utils/viewerTheme';
import { attachCanvasPointer } from './utils/canvasPointer';
import './ViewerCanvas.css';

/* ── Visaren: bryggan mellan React och BIM-motorn (engine/) ──
 * Skapar motorn, laddar IFC-filen och kopplar klick/tryck, hover och dubbelklick/dubbeltryck
 * (pekarlogiken i utils/canvasPointer, så att mus och pekskärm beter sig lika).
 * Kortkommandon ligger på sidnivå (hooks/useBimShortcuts). Renderas bara när en fil finns.
 * Används även av dokumentmodulens IFC-förhandsvisning, därför egen CSS (ViewerCanvas.css)
 * och egen statusslöja (showStatus). BIM-sidan har en egen slöja och skickar showStatus={false}.
 *
 * Motorn (That Open Engine, three.js) laddas först när en fil ska visas (dynamisk import),
 * så att den tunga koden inte följer med sidor som bara importerar komponenten.
 * Varje ny fil eller återställning (resetToken) får en ny motor; den gamla rivs.
 */

const loadEngineFactory = () => import('./engine/createEngine').then((m) => m.default);

function nextFrame() { return new Promise((r) => requestAnimationFrame(() => r())); }

const IDLE = { loading: false, error: '', progress: null };

/** Text i statusslöjan under laddning, t.ex. "Läser IFC… 42 %". */
export function loadingText(status) {
  const p = status?.progress;
  return Number.isFinite(p) ? `Läser IFC… ${p} %` : 'Laddar IFC-modell…';
}

export default function ViewerCanvas({
  onSelect,
  onClear,
  ifcFile,
  resetToken,
  onIfcLoaded,
  onIfcStatusChange,
  gridEnabled = true,
  selectionMode = 'pick',
  xray = false,
  highlightExpressId = null,
  showStatus = true,
}) {
  const containerRef = useRef(null);
  const engineRef = useRef(null);
  const onIfcLoadedRef = useRef(onIfcLoaded);
  const onSelectRef = useRef(onSelect);
  const onClearRef = useRef(onClear);
  const selectionModeRef = useRef(selectionMode);
  const gridEnabledRef = useRef(gridEnabled);
  const xrayRef = useRef(xray);
  // Status förs upp till sidan (onIfcStatusChange) och visas i canvasens slöja om showStatus.
  const [ifcStatus, setIfcStatus] = useState(IDLE);
  const [loadedKey, setLoadedKey] = useState(0);

  useEffect(() => { onIfcLoadedRef.current = onIfcLoaded; }, [onIfcLoaded]);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { onClearRef.current = onClear; }, [onClear]);

  useEffect(() => {
    selectionModeRef.current = selectionMode;
    applyViewerMode(engineRef.current, selectionMode);
  }, [selectionMode]);

  useEffect(() => {
    xrayRef.current = xray;
    try { engineRef.current?.setXray(xray); } catch { /* best-effort */ }
  }, [xray, loadedKey]);

  useEffect(() => {
    gridEnabledRef.current = gridEnabled;
    try { engineRef.current?.setGrid(gridEnabled); } catch { /* best-effort */ }
  }, [gridEnabled, loadedKey]);

  useEffect(() => { onIfcStatusChange?.(ifcStatus); }, [ifcStatus, onIfcStatusChange]);

  // Ljust/mörkt läge: scenens färger följer KanPlans tokens (utils/viewerTheme).
  useEffect(() => observeTheme(() => {
    engineRef.current?.applyTheme(readViewerColors(containerRef.current));
  }), []);

  // Markeringen i 3D följer sidans val: null släpper markeringen.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || engine.modelId == null) return;
    engine.select(highlightExpressId == null ? null : Number(highlightExpressId));
  }, [highlightExpressId, loadedKey]);

  // Ingen fil: sidan ska inte ha någon modellkontext.
  useEffect(() => { if (!ifcFile) onIfcLoadedRef.current?.(null); }, [ifcFile]);

  useEffect(() => {
    if (!ifcFile || !containerRef.current) return undefined;
    const container = containerRef.current;
    let cancelled = false;
    let loading = false;
    let engine = null;
    let pointer = null;
    let offSelect = null;

    const disposeEngine = () => {
      if (!engine) return;
      try { engine.dispose(); } catch { /* best-effort */ }
      if (engineRef.current === engine) engineRef.current = null;
      engine = null;
    };

    // Klick/tryck i Peka-läge: träffat objekt markeras, tomt område släpper markeringen.
    const onTap = async (e) => {
      if (!engine || selectionModeRef.current !== 'pick') return;
      const id = await engine.pickAt(e.clientX, e.clientY);
      if (!cancelled && engine) await engine.select(id, { emit: true });
    };
    const onDoubleTap = (e) => invokeModeAction(engine, selectionModeRef.current, e);
    // Musens dblclick; dubbeltryck med finger/penna hanteras i canvasPointer (undvik dubbelkörning).
    const onDblClick = (e) => {
      if (pointer && pointer.lastPointerType() !== 'mouse') return;
      invokeModeAction(engine, selectionModeRef.current, e);
    };
    // Hover-förval — bara med mus i Peka-läge (pekskärm har ingen hover).
    const onHover = (e) => {
      if (!engine || selectionModeRef.current !== 'pick') return;
      engine.hoverAt(e.clientX, e.clientY);
    };
    const onLeave = () => engine?.hoverAt(null);

    const onEngineSelect = async (id) => {
      if (id == null) { onClearRef.current?.(); return; }
      const selection = await fetchIfcSelection(engine, engine?.modelId, id);
      if (!cancelled) onSelectRef.current?.(selection);
    };

    async function run() {
      // Stoppa icke-IFC-filer innan de når WASM-parsern.
      if (!isIfcFile(ifcFile)) {
        const ext = (ifcFile.name?.split('.').pop() || '').toUpperCase();
        setIfcStatus({ ...IDLE, error: `Filformat ${ext} stöds inte — endast IFC-filer kan öppnas i BIM.` });
        return;
      }
      loading = true;
      setIfcStatus({ loading: true, error: '', progress: 0 });
      try {
        const createEngine = await loadEngineFactory();
        if (cancelled) return;
        engine = await createEngine({ container });
        if (cancelled) return;
        engine.applyTheme(readViewerColors(container));
        engine.setGrid(gridEnabledRef.current);
        applyViewerMode(engine, selectionModeRef.current);
        offSelect = engine.on('select', onEngineSelect);
        pointer = attachCanvasPointer(container, { onTap, onDoubleTap, onHover, onLeave });
        container.addEventListener('dblclick', onDblClick);

        const { modelId } = await engine.loadIfc(ifcFile, {
          onProgress: (p) => { if (!cancelled) setIfcStatus((s) => ({ ...s, progress: p })); },
        });
        if (cancelled) return;
        if (!container.querySelector('canvas')) throw new Error('Visaren kunde inte startas (ingen canvas skapades).');
        engineRef.current = engine;
        engine.setXray(xrayRef.current);

        // Startvy: vänta en frame så att canvasens storlek är satt, passa sedan in hela modellen.
        await nextFrame();
        if (cancelled) return;
        engine.resize();
        await engine.fitAll(false);
        if (cancelled) return;

        onIfcLoadedRef.current?.({ engine, modelId, filename: ifcFile?.name || '' });
        setLoadedKey((k) => k + 1);
        setIfcStatus(IDLE);
      } catch (e) {
        const msg = e?.message ? String(e.message) : 'Kunde inte ladda IFC.';
        if (!cancelled) setIfcStatus({ ...IDLE, error: msg });
      } finally {
        loading = false;
        // Avbruten under laddning: riv motorn först nu (att riva mitt i konverteringen kan krascha).
        if (cancelled) disposeEngine();
      }
    }

    run();

    return () => {
      cancelled = true;
      pointer?.detach();
      pointer = null;
      container.removeEventListener('dblclick', onDblClick);
      offSelect?.();
      if (engineRef.current === engine) engineRef.current = null;
      if (!loading) disposeEngine();
      onIfcLoadedRef.current?.(null);
    };
  }, [ifcFile, resetToken]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="bim-canvas" aria-label="Visare">
      <div
        className="bim-canvas__surface"
        role="application"
        tabIndex={0}
        aria-label="IFC-visare"
        ref={containerRef}
      />
      {showStatus && ifcFile && ifcStatus.loading && (
        <div className="bim-canvas__status" role="status">
          <span className="bim-canvas__spinner" aria-hidden="true" />
          {loadingText(ifcStatus)}
        </div>
      )}
      {showStatus && ifcFile && !ifcStatus.loading && ifcStatus.error && (
        <div className="bim-canvas__status bim-canvas__status--error" role="alert">
          IFC-fel: {ifcStatus.error}
        </div>
      )}
    </section>
  );
}
